import SessionStorageService from "@/services/SessionStorageService";
import { ApiError } from "@/models/share.model";
import router from "@/router";
import { Auth } from "@/models/auth.model";
import { Routes } from "@/models/layout.model";

export const API_PATH: string = process.env.VUE_APP_MEDSERVER_BASE_URL + process.env.VUE_APP_API_PATH;

const redirectLogin = () => {
    SessionStorageService.clear()
    router.push(Routes.LOGIN);
}

export function apiDateTime(date: any): Date {
    if (date instanceof Date)
        return date;

    if (!date || typeof date !== "string" || date.length === 0)
        return null;

    return new Date((date as string).replace("[UTC]", ""))
}

export function apiDate(date: Date | string): Date {
    if (date instanceof Date)
        return date;
    if (!date || typeof date !== "string" || date.length === 0)
        return null;

    return new Date((date as string).replace("Z", ""))
}

export function apiDateString(date: any): string {
    const tsDate:Date = apiDate(date)
    let day:string = "0" + tsDate.getDate().toString()
    day = day.slice(-2)
    const monthInt = tsDate.getMonth() + 1
    let month:string = "0" + monthInt.toString()
    month  = month.slice(-2)
    const year:string = tsDate.getFullYear().toString()
    return `${day}/${month}/${year}`
}

export function apiTimeString(date: any): string {
    const tsDate:Date = apiDate(date)
    let hours:string = "0" + tsDate.getHours().toString()
    hours = hours.slice(-2)
    let minutes:string = "0" + tsDate.getMinutes().toString()
    minutes = minutes.slice(-2)
    let seconds:string = "0" + tsDate.getSeconds().toString()
    seconds = seconds.slice(-2)    
    return `${hours}:${minutes}:${seconds}`
}

export function apiShortTimeString(date: any): string {
    const timeString = apiTimeString(date)
    const [hours, minutes] = timeString.split(":")
    return `${hours}:${minutes}`
}

export const currencyMap: { [key: string]: string } = {
    "EUR": "€",
    "USD": "$",
    "GBP": "£",
    "JPY": "¥",
}


export function apiCurrency(currencyIso: string): string {
    return currencyMap[currencyIso] || currencyIso
}

export function apiAmount(amountStr: string): string {
    // const amount = parseFloat(amountStr);
    // return amount.toFixed(2);    
    const parts = amountStr.split('.')
    if (parts.length === 1) {
        amountStr += ".00"
    } else {
        parts[1] = parts[1].slice(0, 2).padEnd(2, '0')
        amountStr = parts.join('.')
    }
    return amountStr
}

export function apiAmountCurrency(amountStr: string, currencyIso: string): string {
    const currency = apiCurrency(currencyIso)
    const formattedAmount = apiAmount(amountStr)
    return `${formattedAmount} ${currency}`
}

export async function apiSendJson(methodStr: string, endpoint: string, json: any, isBinary = false, getBinaryFull = false): Promise<any | null | ApiError> {
    const start = performance.now();

    const token = SessionStorageService.getItem(Auth.TOKEN);

    if (!token) {
        redirectLogin()
    }

    const headers: HeadersInit = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
    };

    const resp = await fetch(API_PATH + endpoint, {
        method: methodStr,
        headers: headers,
        body: JSON.stringify(json)
    });
    const end = performance.now();
    const duration = end - start;
    // console.log(endpoint + ": " + duration )
    if (resp.ok && !resp.redirected) {
        try {
            if (isBinary) {
                const ret = getBinaryFull ? resp : await resp.blob()
                return ret
            } else {
                const ret = await resp.json()
                return ret
            }
        } catch (e) {
            return null
        }
    } else if (resp.status == 403) {
        return { error : 403}
    }
    
    return Promise.reject(await resp.json());
}

export async function apiSend(methodStr: string, endpoint: string, isBinary = false, getBinaryFull = false): Promise<any | null | ApiError> {
    const start = performance.now();

    const token = SessionStorageService.getItem(Auth.TOKEN);

    if (!token) {
        redirectLogin()
    }

    const headers: HeadersInit = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
    };

    const resp = await fetch(API_PATH + endpoint, {
        method: methodStr,
        headers: headers,
    });
    const end = performance.now();
    const duration = end - start;
    // console.log(endpoint + ": " + duration )
    if (resp.ok && !resp.redirected) {
        try {
            if (isBinary) {
                const ret = getBinaryFull ? resp : await resp.blob()
                return ret
            } else {
                const ret = await resp.json()
                return ret
            }
        } catch (e) {
            return null
        }
    } else if (resp.status == 403) {
        return { error : 403}
    }
    
    return Promise.reject(await resp.json());
}

export async function apiSendJsonAndFiles(methodStr: string, endpoint: string, json: any, files: File[]): Promise<any | null> {
    const token = SessionStorageService.getItem(Auth.TOKEN);

    if (!token) {
        redirectLogin();
    }

    //Create formData
    const formData = new FormData()
    formData.append('json', JSON.stringify(json))
    Array.from(files).forEach((file, index) => {
        const uriFilename = encodeURIComponent(file.name)   //UTF-8 encoding
        formData.append(`file${index}`, file, uriFilename)     
    })

    const headers: HeadersInit = {
        'Authorization': `Bearer ${token}`
    };

    try {
        const resp = await fetch(API_PATH + endpoint, {
            method: methodStr,
            headers: headers,
            body: formData
        });

        if (!resp.ok || resp.redirected) {
            console.error(`API call to ${endpoint} failed with status: ${resp.status}`);
            return null;
        }

        const ret = await resp.json();
        return ret;
    } catch (error) {
        console.error("Error while making API request:", error);
        return null;
    }
}

export async function apiSendFile(methodStr: string, endpoint: string, file: File): Promise<any | null> {
    const token = SessionStorageService.getItem(Auth.TOKEN);

    if (!token) {
        redirectLogin();
    }

    const formData = new FormData();
    const uriFilename = encodeURIComponent(file.name); // UTF-8 encoding
    formData.append('file', file, uriFilename);

    const headers: HeadersInit = {
        'Authorization': `Bearer ${token}`
    };

    const resp = await fetch(API_PATH + endpoint, {
        method: methodStr,
        headers: headers,
        body: formData
    });

    if (resp.ok && !resp.redirected) {
        try {
            const ret = await resp.json();
            return ret;
        } catch (e) {
            return null;
        }
    }
    return Promise.resolve(null);
}

export async function apiSendNamedFiles(methodStr: string, endpoint: string, files: Record<string, File>): Promise<any | null> {
    const token = SessionStorageService.getItem(Auth.TOKEN);

    if (!token) {
        redirectLogin();
    }
    
    const formData = new FormData()
    Object.entries(files).forEach(([fieldName, file]) => {
        const uriFilename = encodeURIComponent(file.name)
        formData.append(fieldName, file, uriFilename)
    })
    
    const headers: HeadersInit = {
        'Authorization': `Bearer ${token}`
    };
    
    const resp = await fetch(API_PATH + endpoint, {
        method: methodStr,
        headers: headers,
        body: formData
    });

    if (resp.ok && !resp.redirected) {
        try {
            const ret = await resp.json();
            return ret;
        } catch (e) {
            return null;
        }
    }
    return Promise.resolve(null);
}